import { useToast } from '@/components/ui/use-toast';
import type { LoginPageQuery, UsersPermissionsUser } from '@edwin-group/cj-graphql/generated/graphql';
import { useAuthentication } from '@/hooks/useAuthentication';
import { login } from '@/services/auth/auth.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { useSuspenseQuery } from '@apollo/client';
import { QUERY_LOGIN_PAGE } from '@edwin-group/cj-graphql';
// import { usePreFetchImage } from '@/hooks/usePreFetchImage';

const formSchema = z.object({
  identifier: z.string().min(1, { message: 'Identifier is required' }),
  password: z.string().min(1, { message: 'Password is required' }),
  rememberMe: z.boolean().optional(),
});

export type FormInputs = z.infer<typeof formSchema>;

export default function useLogin() {
  const { data } = useSuspenseQuery<LoginPageQuery>(QUERY_LOGIN_PAGE, {
    fetchPolicy: 'cache-first',
    context: {
      skipAuth: true,
    },
  });

  const loginPage = data?.loginPage?.data?.attributes;
  // const imageUrl = loginPage?.pageImage?.data?.attributes?.url;
  // const { isImageLoaded } = usePreFetchImage(imageUrl);

  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    handleSubmit,
    reset,
    register,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: zodResolver(
      z.object({
        identifier: z
          .string()
          .min(1, { message: loginPage?.fieldErrors?.identifierRequired || 'Identifier is required' }),
        password: z.string().min(1, { message: loginPage?.fieldErrors?.passwordRequired || 'Password is required' }),
        rememberMe: z.boolean().optional(),
      }),
    ),
    mode: 'onBlur',
  });

  const { loginAuth } = useAuthentication();
  const location = useLocation();
  const isConfirmation = location.pathname.includes('confirmed');
  const queryParams = new URLSearchParams(location.search);
  const callbackUrl = queryParams.get('callbackUrl');

  const onLogin = async (data: {
    identifier: string;
    password: string;
    rememberMe?: boolean;
    errorMessage:
      | {
          title?: string | null;
          message?: string | null;
        }
      | undefined
      | null;
  }) => {
    const result = await login({
      email: data.identifier,
      password: data.password,
      rememberMe: data.rememberMe || false,
    });
    if (result.error) {
      toast({
        title: data?.errorMessage?.title || 'Error signing in',
        description: data?.errorMessage?.message || result.error.message,
        variant: 'destructive',
      });
      return;
    }

    // Set authentication
    loginAuth({
      accessToken: result.data?.token || '',
      isAuthenticated: true,
      user: result.data?.user as UsersPermissionsUser,
    });
    reset();

    const user = result.data?.user;

    if (user?.onboardHomeCompleted) {
      if (callbackUrl && callbackUrl !== '/not-authorized') {
        navigate(callbackUrl);
        return;
      }
      navigate('/');
      return;
    }
    const currentStep = localStorage.getItem(`current-step-${user?.id}`);
    navigate(currentStep || '/on-boarding/step-1');
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    return await onLogin({
      identifier: data.identifier,
      password: data.password,
      rememberMe: data.rememberMe || false,
      errorMessage: loginPage?.popUpMessages?.error,
    });
  };

  return {
    loginPage,
    isConfirmation,
    isLoading: false, //!isImageLoaded,
    handleSubmit: handleSubmit(onSubmit),
    register,
    setValue,
    onLogin,
    errors,
  };
}
